/**
 * Press Box → one game. 2026-10-17.
 *
 * Top to bottom: the score and where it came from; how the official pass was
 * analysed (rules only, or rules + the stats AI on the GPU box — and what the
 * AI changed) with Re-run buttons; WhatsApp updates waiting for the keeper;
 * the stats panel (upload, Fix events with the transcript, voice log); and
 * the play-by-play in tape order.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, RefreshCw, ExternalLink, Mic, Loader2, AlertTriangle, Upload, CalendarClock, FileText, Trash2, Square, StickyNote, Scale , Download } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { chatSocket } from '../../services/socket.js';
import { useAuth } from '../../context/AuthContext.jsx';
import GameStatsPanel from '../../components/stats/GameStatsPanel.jsx';
import PendingAnnouncements from '../../components/PendingAnnouncements.jsx';
import { uploadRecording, uploadError, RECORDING_ACCEPT } from '../../utils/recordingUpload.js';
import PressBoxShell from './PressBoxShell.jsx';
import { PRESS_BOX, modelAlias } from '../../constants/pressBox.js';
import { fmtGameTime } from '../../utils/pressBoxFormat.js';
import { StatusChip, IN_PROGRESS } from './PressBoxGamesPage.jsx';
import ScoreSheetCard from './ScoreSheetCard.jsx';
import GameVideoCard from './GameVideoCard.jsx';

const mmss = (sec) => { if (sec == null) return ''; const s0 = Math.round(sec); return `${Math.floor(s0 / 60)}:${String(s0 % 60).padStart(2, '0')}`; };
const TYPE_WORD = { GOAL: 'Goal', ASSIST: 'Assist', SHOT: 'Shot', SAVE: 'Save', PENALTY: 'Penalty', FACEOFF: 'Faceoff', MISS: 'Miss', BLOCK: 'Block', HIT: 'Hit', CHANCE: 'Chance', SCORE: 'Score said' };

/** Upload a recording into this game — for the coach and the named recorders. */
function UploadButton({ teamId, gameId, onDone, label = 'Upload a recording' }) {
  const ref = useRef(null);
  const [up, setUp] = useState(null);
  const pick = async (file) => {
    if (!file) return;
    setUp({ phase: 'sending', pct: 0 });
    try {
      const { video } = await uploadRecording({ teamId, gameId, file, onProgress: (p) => setUp(u => ({ ...(u || {}), ...p })) });
      toast.success(video ? 'Video uploaded — pulling the sound out, then the stats' : 'Recording uploaded — the stats follow in a few minutes', { duration: 7000 });
      onDone?.();
    } catch (e) {
      toast.error(uploadError(e), { duration: 8000 });
    } finally { setUp(null); }
  };
  return (
    <>
      <input ref={ref} type="file" accept={RECORDING_ACCEPT} style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; pick(f); }} />
      <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={!!up} onClick={() => ref.current?.click()}
        title="Audio (voice memo, m4a, mp3, wav) or a video — only the sound is kept">
        {up ? <><Loader2 size={13} className="animate-spin" /> {up.phase === 'sending' ? `Uploading ${up.pct || 0}%` : 'Processing…'}</> : <><Upload size={13} /> {label}</>}
      </button>
    </>
  );
}

const BUSY_WORD = { training: 'a training run held the card', downloading: 'the model was being installed', slot: 'other work held it the whole time', queue: 'too many requests were queued' };

function AnalysisCard({ teamId, gameId, recording, aiAnalysis, progress, onRerun, canRecord, pbRole }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  // 2026-10-19 — while the pass runs, say how long it has been at it.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!recording || !IN_PROGRESS.includes(recording.engine)) return undefined;
    const t = setInterval(() => tick(x => x + 1), 5000);
    return () => clearInterval(t);
  }, [recording?.id, recording?.engine]);   // eslint-disable-line react-hooks/exhaustive-deps
  // The pass's own start (stamped when the engine took the job) — the
  // recording's startedAt is when the TAPE started and read hours wrong.
  const passStart = recording?.analysis?.passStartedAt || null;
  const elapsedMin = passStart ? Math.max(0, Math.round((Date.now() - new Date(passStart).getTime()) / 60000)) : null;

  // The transcript, as a plain-text file (what the engine heard, with times).
  const downloadTranscript = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/recordings/${recording.id}/transcript`);
      const segs = data?.segments || data?.transcript || [];
      const mm = (sec) => { const s0 = Math.round(Number(sec) || 0); return `${Math.floor(s0 / 60)}:${String(s0 % 60).padStart(2, '0')}`; };
      const text = (Array.isArray(segs) ? segs : [])
        .map(sg => `[${mm(Number(sg.t) || 0)}] ${sg.text || ''}`).join('\n');
      if (!text) { toast.error('No transcript yet — run a pass first'); return; }
      const blob = new Blob([text], { type: 'text/plain' });
      const a2 = document.createElement('a');
      a2.href = URL.createObjectURL(blob);
      a2.download = `transcript-${gameId.slice(0, 8)}.txt`;
      a2.click();
      setTimeout(() => URL.revokeObjectURL(a2.href), 5000);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not fetch the transcript');
    } finally { setBusy(false); }
  };

  // Super admin only (the server checks too): one JSON with everything about
  // this pass - transcript, analysis, events, roster, log - for troubleshooting
  // and quality comparison against other transcription tools.
  const downloadDebugJson = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/recordings/${recording.id}/debug-json`, { responseType: 'blob', timeout: 120000 });
      const a2 = document.createElement('a');
      a2.href = URL.createObjectURL(res.data);
      a2.download = `pressbox_debug_${recording.id.slice(0, 8)}.json`;
      a2.click();
      setTimeout(() => URL.revokeObjectURL(a2.href), 5000);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not build the troubleshooting file');
    } finally { setBusy(false); }
  };

  const stopPass = async () => {
    if (!window.confirm(`Stop ${PRESS_BOX.AI_NAME}?\n\nNothing partial is written; the stretches it already read are kept, so a later re-run only pays for the rest.`)) return;
    try {
      await api.post(`/teams/${teamId}/press-box/games/${gameId}/reanalyze/stop`);
      toast.success('Stopped and taken off the queue — it stays off the GPU until you press Re-run');
      setTimeout(() => onRerun?.(), 4000);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not stop it');
    }
  };

  const deleteRecording = async () => {
    if (!window.confirm('Delete this recording?\n\nThe audio and the stats it produced are deleted for good. Stats you entered by hand stay.')) return;
    setBusy(true);
    try {
      await api.delete(`/teams/${teamId}/press-box/recordings/${recording.id}`);
      toast.success('Recording deleted');
      onRerun?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not delete the recording');
    } finally { setBusy(false); }
  };
  const a = recording?.analysis || null;
  const running = !!recording && IN_PROGRESS.includes(recording.engine) && recording.status !== 'recording';
  const rerun = async (body, label) => {
    // Still "being worked out": usually it is, and the engine says so (409).
    // But if that has gone on for a long time, starting again is the way out.
    if (running && !window.confirm('These stats are still being worked out. Start again anyway? (If a pass is still running, the engine will say so.)')) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/press-box/games/${gameId}/reanalyze`, { ...body, recordingId: recording?.id });
      toast.success(`${label} started${data.transcript === 'reused' ? ' (same transcript)' : ''} — a few minutes`, { duration: 6000 });
      onRerun?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not start it', { duration: 8000 });
    } finally { setBusy(false); }
  };
  if (!recording) {
    return (
      <div className="card mb-4 text-sm flex items-center justify-between gap-3" style={{ color: 'var(--text-2)', flexWrap: 'wrap' }}>
        <span>No recording for this game yet. {canRecord ? 'Upload one, or record the game live.' : 'The coach or a designated stat recorder uploads or records it.'}</span>
        {canRecord && <UploadButton teamId={teamId} gameId={gameId} onDone={onRerun} />}
      </div>
    );
  }
  const d = a?.diff || null;
  const off = busy || recording.status === 'recording';
  return (
    <div className="card mb-4" data-analysis-card>
      <div className="flex items-center justify-between gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
        <div className="flex items-center gap-2 font-bold text-sm"><Sparkles size={15} style={{ color: 'var(--gold)' }} /> How these stats were worked out</div>
        <div className="flex items-center gap-1" style={{ flexWrap: 'wrap' }}>
          {(running || recording.aiQueued) && (
            <button type="button" className="btn-danger" style={{ padding: '6px 10px' }}
              onClick={stopPass} title={running
                ? 'Stop the running analysis and take it off the queue — nothing partial is written; Re-run starts it again'
                : 'Take this game out of the AI queue — it stays off the GPU until you press Re-run'}>
              <Square size={13} /> {running ? 'Stop' : 'Take off the queue'}
            </button>
          )}
          <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={off}
            onClick={() => rerun({ analysis: 'ai' }, `${PRESS_BOX.AI_NAME} analysis`)} title={`Run ${PRESS_BOX.AI_NAME} over the transcript again (keeps your hand edits)`}>
            <RefreshCw size={13} /> Re-run {PRESS_BOX.AI_NAME}
          </button>
          <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={off}
            onClick={() => { if (running || window.confirm('Decode the whole recording again (a few minutes), then analyse it?')) rerun({ fresh: true }, 'New transcription'); }}
            title="Transcribe the recording again (e.g. after a speech-to-text change)">
            Transcribe again
          </button>
          {canRecord && <UploadButton teamId={teamId} gameId={gameId} onDone={onRerun} label="Upload another" />}
          <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={off}
            onClick={downloadTranscript} title="Download everything the engine heard, with tape times, as a text file">
            <FileText size={13} /> Transcript
          </button>
          {user?.role === 'admin' && (
            <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={busy}
              onClick={downloadDebugJson} title="Troubleshooting JSON (super admin): the transcript, analysis, events, roster and log in one file">
              <FileText size={13} /> Debug JSON
            </button>
          )}
          {pbRole === 'admin' && (
            <button type="button" className="btn-ghost" style={{ padding: '6px 10px', color: 'var(--penalty)' }} disabled={busy || recording.status === 'recording'}
              onClick={deleteRecording} title="Delete this recording and its stats for good">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>
      {running && (() => {
        // 2026-10-19 — phase + elapsed + estimate. The engine reports
        // progress 0→0.7 while transcribing and 0.72→0.99 while Press Box AI
        // reads the windows; the message carries the minutes.
        const p = typeof progress?.progress === 'number' ? progress.progress : null;
        // 2026-11-02 (per Gilles) — the model's calls stand in the GPU
        // broker's line behind a video run: say "in line", like a video does
        const inLine = /in line for the card/i.test(progress?.message || '');
        const phase = recording.engine === 'stt_wait'
          ? 'Waiting for a free GPU (Whisper)'
          : inLine ? `${PRESS_BOX.AI_NAME} in line for the GPU`
          : p != null
          ? (p < 0.71 ? 'Transcribing' : `${PRESS_BOX.AI_NAME} analysing`)
          : (/transcrib/i.test(progress?.message || '') ? 'Transcribing'
            : /AI analysis|reading/i.test(progress?.message || '') ? `${PRESS_BOX.AI_NAME} analysing` : 'Working');
        const elapsedS = passStart ? Math.max(1, (Date.now() - new Date(passStart).getTime()) / 1000) : null;
        // 2026-10-27 (per Gilles: "estimating never works") — when the engine
        // reports a progress number, project from it; when it does not, use
        // the LAST finished pass on this recording as the yardstick (same
        // tape, same model — the best predictor there is). Never print the
        // word "estimating": either a number or nothing.
        const projected = elapsedS != null && p > 0.05 ? Math.ceil((elapsedS * (1 - p)) / p / 60) : null;
        const prevSecs = a?.seconds || null;
        const etaMin = projected != null ? projected
          : (prevSecs && elapsedS != null ? Math.ceil(Math.max(0, prevSecs - elapsedS) / 60) : null);
        const almostDone = projected == null && prevSecs && elapsedS != null && elapsedS >= prevSecs;
        return (
          <div className="mb-2">
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--ice)', flexWrap: 'wrap' }}>
              <Loader2 size={14} className="animate-spin" />
              <strong>{phase}{p != null ? ` · ${Math.round(p * 100)}%` : ''}</strong>
              {/* 2026-10-19 (per Gilles) — time spent and the estimate live
                  right beside the word Working, not on a second line. */}
              <span className="text-xs" style={{ color: 'var(--text-2)' }}>
                {elapsedMin != null ? `${elapsedMin < 1 ? 'under a minute' : `${elapsedMin} min`} so far` : 'just started'}
                {almostDone ? ' · about as long as the last pass took — any minute now'
                  : etaMin != null ? ` · about ${etaMin < 1 ? 1 : etaMin} min to go${projected == null ? ' (going by the last pass)' : ''}` : ''}
              </span>
            </div>
            {progress?.message && <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>{progress.message}</div>}
            {elapsedMin >= 25 && (
              <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                A long game or a busy GPU — it keeps going by itself (Stop ends it).
              </div>
            )}
            {p != null && (
              <div style={{ height: 4, borderRadius: 2, background: 'var(--surface-2)', marginTop: 6, overflow: 'hidden' }}>
                <div style={{ width: `${Math.round(p * 100)}%`, height: '100%', background: 'var(--ice)', transition: 'width .5s' }} />
              </div>
            )}
          </div>
        );
      })()}
      {recording.engine === 'batch_error' && (
        <div className="flex items-start gap-2 text-sm mb-2" style={{ color: 'var(--penalty)' }}>
          <AlertTriangle size={14} style={{ marginTop: 2 }} /> The after-game pass failed: {recording.error || 'unknown error'}
        </div>
      )}
      {a ? (
        <div className="text-sm" style={{ color: 'var(--text-2)' }}>
          {a.mode === 'rules+llm' ? (
            <div>
              <span className="badge" style={{ background: 'var(--goal-a10)', color: 'var(--goal)', border: '1px solid var(--goal-a20)', marginRight: 6 }}>{PRESS_BOX.AI_NAME} ✓</span>
              Rules first, then <strong style={{ color: 'var(--text)' }}>{modelAlias(a.model) || PRESS_BOX.AI_NAME}</strong> read the transcript in {a.windows ?? '?'} stretches
              {a.fallbackWindows ? ` (${a.fallbackWindows} fell back to the rules)` : ''}
              {a.llmSeconds != null ? ` — ${Math.round(a.llmSeconds / 60) || 1} min on the model` : ''}{a.seconds != null ? `, ${Math.round(a.seconds / 60) || 1} min for the whole pass` : ''}.
              {a.interrupted ? <span style={{ color: 'var(--puck)' }}> It stopped part way ({a.interrupted}); the rest of the game is the rules' for now. It is in the queue and finishes by itself — the stretches it already read are kept.</span> : null}
              {a.error && a.cached ? <span style={{ color: 'var(--puck)' }}> It could not be reached this time, so its earlier answers for this same transcript were used ({a.error}).</span>
                : (a.cached ? <span style={{ color: 'var(--text-3)' }}> {a.cached} of them reused from the last run (same transcript, same model{a.cached >= (a.windows || 0) ? ' — no GPU call was needed' : ''}).</span> : null)}
              {a.queuedSeconds >= 30 ? <span style={{ color: 'var(--text-3)' }}> Its calls waited {Math.round(a.queuedSeconds / 60) || 1} min in line for the GPU (a video run or another pass had the card).</span> : null}</div>
          ) : (
            <div>
              <span className="badge" style={{ background: 'var(--puck-a10)', color: 'var(--puck)', border: '1px solid var(--puck-a20)', marginRight: 6 }}>{PRESS_BOX.AI_NAME} not run</span>
              Rules only{a.seconds != null ? ` (${Math.round(a.seconds / 60) || 1} min)` : ''}{a.requested === 'ai' ? (a.busy
              ? <> — <span style={{ color: 'var(--puck)' }}>its GPU was busy ({BUSY_WORD[a.busy.kind] || 'in use'})</span></>
              : <> — <span style={{ color: 'var(--puck)' }}>{PRESS_BOX.AI_NAME} has not read this transcript: {a.interrupted || a.error || 'no answer'}</span></>) : ''}.</div>
          )}
          {/* 2026-10-18 — the broker said "busy": the AI check is booked again */}
          {recording.aiHold ? (
            <div className="text-xs mt-1" style={{ color: 'var(--text-2)' }}>
              On hold — this game is out of the AI queue and won't go to the GPU. Re-run {PRESS_BOX.AI_NAME} starts it again.
            </div>
          ) : recording.inLine ? (
            <div className="text-xs mt-1" style={{ color: 'var(--ice)' }}>
              In line for the 5090 — this {recording.inLine.fresh ? 'transcribe + analyse' : 'Press Box AI'} re-run starts by itself when the card is free (one GPU job at a time{recording.inLine.at ? `; asked ${new Date(recording.inLine.at).toLocaleString([], { hour: 'numeric', minute: '2-digit' })}` : ''}).
            </div>
          ) : recording.aiQueued && recording.aiRetryAt ? (
            <div className="text-xs mt-1" style={{ color: 'var(--puck)' }}>
              Queued — the stats AI checks this game again by itself at about {new Date(recording.aiRetryAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })} — the result here is the rules' until then.
            </div>
          ) : a.retryAt && !recording.aiHold ? (
            <div className="text-xs mt-1" style={{ color: 'var(--puck)' }}>
              The stats AI checks this game again by itself at about {new Date(a.retryAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })} — the result here is the rules' until then.
            </div>
          ) : null}
          {a.handGoalsMatched ? (
            <div className="text-xs mt-1" style={{ color: 'var(--text-2)' }}>
              {a.handGoalsMatched} goal{a.handGoalsMatched === 1 ? '' : 's'} added by hand (the + button or Fix events) matched to the goal{a.handGoalsMatched === 1 ? '' : 's'} this pass heard — counted once.
            </div>
          ) : null}
          <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
            {a.transcript === 'reused' ? 'Same transcript as before' : 'Transcribed from the recording'}
            {(a.decodedOn || a.heard?.on) === 'gpu'
              ? ` · heard by ${(() => {
                  // 2026-10-27 (per Gilles) — name the card, nothing more.
                  const h = a.heard || null;
                  if (!h) return 'Whisper (GPU)';
                  const sh = Number(h.shared) || 0, ch = Number(h.chunks) || 0;
                  if (sh > 0 && ch > 0) return 'Whisper 5090 + 1070';
                  if (sh > 0) return 'Whisper 5090';
                  if (ch > 0) return 'Whisper 1070';
                  return 'Whisper (GPU)';
                })()}`
              : ' · heard on the CPU — Re-run upgrades to Whisper on the GPU when a card is free'}
            {a.tapeSeconds ? ` · ${Math.round(a.tapeSeconds / 60)} min of tape` : ''}{a.finishedAt ? ` · ${new Date(a.finishedAt).toLocaleString()}` : ''}
          </div>
          {d && (d.added?.length || d.removed?.length || d.changed?.length) ? (
            <details className="mt-2">
              <summary className="text-xs font-semibold" style={{ cursor: 'pointer', color: 'var(--text)' }}>
                What the AI changed: {d.added?.length || 0} added, {d.removed?.length || 0} dropped, {d.changed?.length || 0} changed
              </summary>
              <div className="text-xs mt-2" style={{ display: 'grid', gap: 4 }}>
                {(d.added || []).map((x, i) => <div key={`a${i}`}><span style={{ color: 'var(--goal)' }}>+ {mmss(x.t)} {TYPE_WORD[x.type] || x.type} {x.team === 'THEM' ? '(them)' : ''}{x.player != null ? ` #${x.player}` : ''}</span>{x.why ? <span style={{ color: 'var(--text-3)' }}> — {x.why}</span> : null}</div>)}
                {(d.removed || []).map((x, i) => <div key={`r${i}`} style={{ color: 'var(--penalty)' }}>− {mmss(x.t)} {TYPE_WORD[x.type] || x.type} {x.team === 'THEM' ? '(them)' : ''}{x.player != null ? ` #${x.player}` : ''}</div>)}
                {(d.changed || []).map((x, i) => <div key={`c${i}`}><span style={{ color: 'var(--puck)' }}>~ {mmss(x.rule?.t)} {TYPE_WORD[x.rule?.type] || x.rule?.type}: #{x.rule?.player ?? '?'} {x.rule?.team || ''} → #{x.llm?.player ?? '?'} {x.llm?.team || ''}</span>{x.why ? <span style={{ color: 'var(--text-3)' }}> — {x.why}</span> : null}</div>)}
              </div>
            </details>
          ) : null}
        </div>
      ) : (!running && <div className="text-sm" style={{ color: 'var(--text-2)' }}>{aiAnalysis ? 'Not analysed yet.' : 'Not analysed yet. AI analysis is off for this team (Settings).'}</div>)}
    </div>
  );
}

/**
 * 2026-10-18 — the tape named a different game. After a batch pass on a
 * standalone Press Box team, the engine listens for the narrator's opening
 * ("It's Sunday, October 18th, 9 pm…"); when that date names another
 * scheduled game, this banner offers to move the recording there. Nothing
 * moves without this click.
 */
function TapeGameBanner({ teamId, recording, onMoved }) {
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const navigate = useNavigate();
  const tg = recording?.analysis?.tapeGame;
  if (!tg?.gameId || hidden) return null;
  const move = async () => {
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/press-box/recordings/${recording.id}/move`, { gameId: tg.gameId });
      toast.success('Recording moved — the stats are on the right game now');
      navigate(`/press-box/${teamId}/game/${tg.gameId}`);
      onMoved?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not move the recording');
      setBusy(false);
    }
  };
  return (
    <div className="card mb-4" style={{ border: '1px solid var(--gold)', background: 'var(--surface)' }} data-tape-game>
      <div className="flex items-center gap-2 font-bold mb-1"><CalendarClock size={16} style={{ color: 'var(--gold)' }} /> The tape says this is a different game</div>
      <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
        The recording opens with <strong>{tg.date}{tg.spokenTime ? ` at ${tg.spokenTime}` : ''}{tg.opponent ? ` vs ${tg.opponent}` : ''}</strong>,
        which matches another game on your schedule. Move the recording and its stats there?
      </div>
      <div className="flex items-center gap-2">
        <button type="button" className="btn-ice" style={{ padding: '7px 12px' }} onClick={move} disabled={busy}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <CalendarClock size={13} />} Move it there
        </button>
        <button type="button" className="btn-ghost" style={{ padding: '7px 10px' }} onClick={() => setHidden(true)} disabled={busy}>Keep it here</button>
      </div>
    </div>
  );
}

export default function PressBoxGamePage() {
  const { teamId, gameId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/teams/${teamId}/press-box/games/${gameId}`);
      setData(d);
    } catch (e) {
      setData({ error: e.response?.status === 404 ? 'Game not found' : (e.response?.data?.error || 'Could not load the game') });
    }
  }, [teamId, gameId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    chatSocket.joinTeam(teamId);
    // A goal in mini-game 2+ carries the mini-game's id and the root's.
    const mine = (d) => d && (String(d.gameId) === String(gameId) || String(d.rootGameId || '') === String(gameId));
    const offs = [
      chatSocket.on('GAME_STATS_PROGRESS', (d) => { if (mine(d)) { setProgress(d); setData(x => x && x.recordings?.[0] && x.recordings[0].engine !== 'batch' ? { ...x, recordings: [{ ...x.recordings[0], engine: 'batch' }, ...x.recordings.slice(1)] } : x); } }),
      chatSocket.on('GAME_STATS_FINAL', (d) => { if (mine(d)) { setProgress(null); load(); } }),
      chatSocket.on('GAME_STATS_ERROR', (d) => { if (mine(d)) { setProgress(null); load(); } }),
      chatSocket.on('GAME_STATS_GOAL', (d) => { if (mine(d)) load(); }),
      chatSocket.on('GAME_STATS_PLAY', (d) => { if (mine(d)) load(); }),
    ];
    return () => offs.forEach(f => f?.());
  }, [teamId, gameId, load]);
  // While it is being recorded or analysed, look again every 30 s: the socket
  // can miss the end (a phone asleep, a socket that gave up reconnecting).
  const working = !!data && !data.error && (!!data.live || (data.recordings || []).some(r => IN_PROGRESS.includes(r.engine)));
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [working, load]);

  if (!data) return <PressBoxShell teamId={teamId} userId={user?.id}><div className="text-secondary text-sm p-4">Loading…</div></PressBoxShell>;
  if (data.error) return <PressBoxShell teamId={teamId} userId={user?.id}><div className="card text-center p-4">{data.error}</div></PressBoxShell>;

  const g = data.game;
  const rec = (data.recordings || []).find(r => r.role === 'primary') || data.recordings?.[0] || null;
  // Editing stats is the coach's or a manager's (the game-events API says so);
  // a stat keeper uploads and sends WhatsApp updates.
  const isCoach = ['coach', 'manager'].includes(data.role);
  const statusGame = { live: data.live, events: (data.plays || []).length, recording: rec ? { engine: rec.engine, analysis: rec.analysis, error: rec.error } : null };

  return (
    <PressBoxShell teamId={teamId} userId={user?.id}>
      <Link to={`/press-box/${teamId}`} className="btn-ghost -ml-1 mb-3" style={{ padding: '6px 10px' }}><ArrowLeft size={14} /> All games</Link>

      <div className="card mb-4">
        <div className="flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
          <div>
            <div className="text-xs" style={{ color: 'var(--text-3)' }}>{g.date} · {fmtGameTime(g.time, data.timeFormat)}</div>
            <div className="font-bold text-lg">vs {g.opponent || 'opponent not set'}</div>
            <div className="flex items-center gap-2 mt-1" style={{ flexWrap: 'wrap' }}>
              <StatusChip game={statusGame} />
              {/* 2026-10-22 (per Gilles) — the publish state, right at the top */}
              {rec?.engine === 'final' && (data.publishedAt
                ? <span className="badge" title={`On the family page since ${new Date(data.publishedAt).toLocaleString()}`}
                    style={{ background: 'var(--goal-a10)', color: 'var(--goal)', border: '1px solid var(--goal-a20)' }}>Published{data.publicEnabled ? '' : ' (page off)'}</span>
                : <span className="badge" title={data.publishMode === 'auto' ? 'Publishes by itself when the analysis completes' : 'Waiting for your review — publish it from the Publish tab or after previewing below'}
                    style={{ background: 'var(--puck-a10)', color: 'var(--puck)', border: '1px solid var(--puck-a20)' }}>Not published{data.publishMode === 'review' ? ' — awaiting review' : ' yet'}</span>)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="display-num" style={{ fontSize: 44 }}>{data.score ? `${data.score.us}–${data.score.them}` : '–'}</div>
            {/* 2026-10-27 (per Gilles, Burlington 7–2) — say WHERE the number
                came from: until a league/confirmed final is on file, the big
                score is what the AI counted on the tape. */}
            {data.score && !data.leagueScore && (
              <div className="text-xs" style={{ color: 'var(--text-3)', marginTop: -4 }} data-tape-score
                title="Counted from the recording by the stats AI — not confirmed yet. The league webcal, the official score sheet or your confirmation below makes it final.">
                from the tape — not confirmed
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
          {data.canRecord && <Link to={`/team/${teamId}/game/${gameId}/live`} className="btn-ghost" style={{ padding: '6px 10px' }}><Mic size={13} /> Record &amp; track live</Link>}
          {rec && (
            <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }}
              title="Download the game's full audio as one file (can take a moment for a long game)"
              onClick={async (e) => {
                const btn = e.currentTarget; btn.disabled = true;
                try {
                  const res = await api.get(`/teams/${teamId}/press-box/recordings/${rec.id}/audio`, { responseType: 'blob' });
                  const a2 = document.createElement('a');
                  a2.href = URL.createObjectURL(res.data);
                  a2.download = `${g.date || 'game'}_vs_${(g.opponent || 'opponent').replace(/[^\w-]+/g, '_')}.m4a`;
                  a2.click();
                  setTimeout(() => URL.revokeObjectURL(a2.href), 10000);
                } catch { toast.error('Could not build the audio file'); }
                finally { btn.disabled = false; }
              }}>
              <Download size={13} /> Download audio
            </button>
          )}
          <Link to={`/game/${gameId}`} className="btn-ghost" style={{ padding: '6px 10px' }}>Game page</Link>
          <Link to={`/press-box/${teamId}/game/${gameId}/preview`} className="btn-ghost" style={{ padding: '6px 10px' }}
            title="See this game exactly as the families will — works before publishing too (a draft carries a clear banner)">
            <ExternalLink size={13} /> Preview family view
          </Link>
          {data.publicUrl && <a href={data.publicUrl} target="_blank" rel="noreferrer" className="btn-ghost" style={{ padding: '6px 10px' }}><ExternalLink size={13} /> Public page</a>}
        </div>
      </div>

      {isCoach && rec && <TapeGameBanner teamId={teamId} recording={rec} onMoved={load} />}

      {/* 2026-10-22 (per Gilles) — no webcal/league final: the narrator
          confirms the final score, and goal reconciliation anchors to it. */}
      {/* 2026-10-24 (per Gilles) — the league webcal is read for the final
          30 min after the game, then every 30 min for 3 days; after that the
          narrator is asked to confirm it (and check it against the tape). */}
      {!data.leagueScore && data.scoreFeed?.state === 'waiting' && (
        <div className="card mb-4 text-sm" style={{ color: 'var(--text-2)' }} data-feed-waiting>
          <CalendarClock size={14} style={{ display: 'inline', verticalAlign: '-2px', color: 'var(--ice)' }} />{' '}
          {/* 2026-10-27 (per Gilles) — a tournament game: say so instead of
              promising a webcal final that never comes. */}
          {data.tournament
            ? <>This is a tournament game (<strong>{data.tournament}</strong>) — the league calendar never posts
              tournament finals, so confirm the score below or upload the official score sheet.</>
            : <>Waiting for the league&rsquo;s
              final from the webcal — checked every 30 minutes{data.scoreFeed.nextCheckAt ? ` (next ${new Date(data.scoreFeed.nextCheckAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})` : ''}
              {data.scoreFeed.until ? `, until ${new Date(data.scoreFeed.until).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}` : ''}.
              You can also confirm it yourself below, or apply the official score sheet.</>}
        </div>
      )}
      {!data.leagueScore && (rec?.engine === 'final' || data.scoreFeed?.state === 'gave_up') && (
        <ConfirmFinalCard teamId={teamId} gameId={gameId} tape={data.score} onSaved={load}
          gaveUp={data.scoreFeed?.state === 'gave_up'} feedNote={data.scoreFeed?.note || null}
          tournament={data.tournament || null} />
      )}

      <ScoreSheetCard teamId={teamId} gameId={gameId} canEdit={['admin', 'edit'].includes(data.pbRole) || isCoach} onApplied={load} />

      {/* 2026-10-26 (per Gilles) — the arena camera's video + highlight clips */}
      <GameVideoCard teamId={teamId} gameId={gameId} canRecord={!!data.canRecord} canEdit={['admin', 'edit'].includes(data.pbRole) || isCoach} />

      {/* 2026-10-19 — the league's final score (webcal / Save) is the truth;
          when the tape counted something else, say so instead of hiding it. */}
      {data.scoreMismatch && (
        <div className="card mb-4" style={{ border: '1px solid var(--puck)' }} data-score-mismatch>
          <div className="flex items-center gap-2 font-bold mb-1"><Scale size={15} style={{ color: 'var(--puck)' }} /> The league score and the tape disagree</div>
          <div className="text-sm" style={{ color: 'var(--text-2)' }}>
            The league&rsquo;s final is <strong>{data.scoreMismatch.league.us}–{data.scoreMismatch.league.them}</strong> (shown everywhere),
            but the tape counted <strong>{data.scoreMismatch.tape.us}–{data.scoreMismatch.tape.them}</strong> — a goal was likely missed or
            misheard. Check the goals below and fix them with <strong>Fix events</strong>; the league score stays the official one.
          </div>
        </div>
      )}

      <AnalysisCard teamId={teamId} gameId={gameId} recording={rec} aiAnalysis={data.aiAnalysis} progress={progress} onRerun={load} canRecord={!!data.canRecord} pbRole={data.pbRole} />

      {/* 2026-10-19 — the colour of the game, straight off the tape: a lost
          tooth, a bench penalty to the coach… collected by Press Box AI. */}
      {rec?.analysis?.notes?.length > 0 && (
        <div className="card mb-4" data-game-notes>
          <div className="flex items-center gap-2 font-bold text-sm mb-2"><StickyNote size={15} style={{ color: 'var(--gold)' }} /> Game notes <span className="text-xs font-normal" style={{ color: 'var(--text-3)' }}>heard by {PRESS_BOX.AI_NAME}</span></div>
          {rec.analysis.notes.map((n, i) => (
            <div key={i} className="flex items-baseline gap-3 text-sm py-1" style={{ borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)', minWidth: 44 }}>{mmss(n.t)}</span>
              <span style={{ color: 'var(--text-2)' }}>{n.text}</span>
            </div>
          ))}
        </div>
      )}

      <PendingAnnouncements teamId={teamId} gameId={gameId} />

      <GameStatsPanel teamId={teamId} gameId={gameId} canEdit={isCoach} />

      {(data.plays || []).length > 0 && (
        <div className="card mb-4" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="px-3 py-2 text-xs font-bold" style={{ color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>
            Play-by-play
          </div>
          {data.plays.map((p, i) => (
            <div key={p.id || i} className="flex items-baseline gap-3 px-3 py-1.5 text-sm" style={{ borderBottom: '1px solid var(--border)' }} title={p.said || ''}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)', minWidth: 48 }}>{mmss(p.t)}</span>
              <span style={{ fontSize: 12, color: 'var(--text-3)', minWidth: 22 }}>{p.period ? `P${p.period}` : ''}</span>
              <span style={{ fontWeight: p.type === 'goal' || p.type === 'goal_against' ? 800 : 500, color: p.type === 'goal' ? 'var(--goal)' : (p.type === 'goal_against' ? 'var(--penalty)' : 'var(--text)') }}>
                {p.label}{p.side === 'them' && p.type !== 'goal_against' ? ' (them)' : ''}
              </span>
              <span className="flex-1" style={{ color: 'var(--text-2)' }}>
                {p.player || ''}{p.assists?.length ? ` · from ${p.assists.join(', ')}` : ''}
              </span>
              {p.review && <span className="badge" style={{ background: 'var(--puck-a10)', color: 'var(--puck)', border: '1px solid var(--puck-a20)' }}>check</span>}
              {p.source === 'manual' && <span className="text-xs" style={{ color: 'var(--text-3)' }}>by hand</span>}
            </div>
          ))}
        </div>
      )}
    </PressBoxShell>
  );
}


/** "What was the final?" — saves the league final for reconciliation. */
function ConfirmFinalCard({ teamId, gameId, tape, onSaved, gaveUp = false, feedNote = null, tournament = null }) {
  const [us, setUs] = useState(tape ? String(tape.us) : '');
  const [them, setThem] = useState(tape ? String(tape.them) : '');
  const [busy, setBusy] = useState(false);
  return (
    <div className="card mb-4" style={{ border: `1px solid ${gaveUp ? 'var(--puck)' : 'var(--gold)'}` }} data-confirm-final data-final-needed={gaveUp ? '1' : undefined}>
      <div className="font-bold mb-1 text-sm">{gaveUp ? <><AlertTriangle size={14} style={{ display: 'inline', verticalAlign: '-2px', color: 'var(--puck)' }} /> Final score needed</> : 'Confirm the final score'}</div>
      <div className="text-sm mb-2" style={{ color: 'var(--text-2)' }}>
        {gaveUp
          ? <>{tournament
                ? <>This is a tournament game (<strong>{tournament}</strong>) — the league calendar never posts tournament finals.</>
                : feedNote === 'no_entry'
                ? <>This game is not on the league calendar at all (tournament games never get a webcal score), so there is no score to pick up.</>
                : feedNote === 'no_score'
                  ? <>The league calendar lists this game but still shows no score (checked every 30 minutes for 3 days).</>
                  : <>The league calendar still had no final score 3 days after the game (it was checked every 30 minutes).</>}{' '}
              Confirm the final{tape ? <> — the tape counted <strong>{tape.us}–{tape.them}</strong></> : ''}, and check it against the
              recording (listen to the goals below). Or upload the official score sheet.</>
          : <>There is no league (webcal) final on file for this game, so the goal totals have nothing to
              reconcile to{tape ? <> — the tape counted <strong>{tape.us}–{tape.them}</strong></> : ''}. Type the real
              final and save it; the next AI pass anchors the goals to it.</>}
      </div>
      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
        <label className="text-xs" style={{ color: 'var(--text-3)' }}>Us
          <input type="number" min="0" max="99" className="input-field" style={{ width: 70, marginLeft: 6, padding: '6px 8px' }} value={us} onChange={(e) => setUs(e.target.value)} /></label>
        <label className="text-xs" style={{ color: 'var(--text-3)' }}>Them
          <input type="number" min="0" max="99" className="input-field" style={{ width: 70, marginLeft: 6, padding: '6px 8px' }} value={them} onChange={(e) => setThem(e.target.value)} /></label>
        <button type="button" className="btn-ice" disabled={busy || us === '' || them === ''} onClick={async () => {
          setBusy(true);
          try {
            await api.post(`/teams/${teamId}/press-box/games/${gameId}/final`, { us: Number(us), them: Number(them) });
            toast.success('Final score saved — re-run Press Box AI to reconcile the goals to it');
            onSaved?.();
          } catch (e) { toast.error(e.response?.data?.error || 'Could not save it'); }
          finally { setBusy(false); }
        }}>Save the final</button>
      </div>
    </div>
  );
}
